import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
} from "@mui/material";

import PaymentSummary from "./ItemCard";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { callActiveInactiveCron, checkPrivileges } from "../../Helpers";
import ConfirmDialoag from "../common/ConfirmDialoag";
import instance from "../../axios";

const COLUMN_TEMPLATE = "2fr 0.8fr 1fr 1fr 1fr 1.2fr 1.2fr";

const rowSx = {
    display: "grid",
    gridTemplateColumns: COLUMN_TEMPLATE,
    columnGap: 1,
    alignItems: "center",
    px: 3,
    py: 1,
};

const ListItemsInvoicebase = ({ open, onClose, selectedPaymentItem, getInvoices }) => {
    const [visibleCount, setVisibleCount] = useState(6);
    const [selectedPaymentId, setSelectedPaymentId] = useState(null);
    const [invoicePayments, setInvoicePayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAllocatedConfirm, setIsAllocatedConfirm] = useState(false);
    const [isRejectConfirm, setIsRejectConfirm] = useState(false);

    const handleLoadMore = () => {
        setVisibleCount((prev) => prev + 3);
    };

    const fetchInvoicePayments = async () => {
        if (!selectedPaymentItem) return;
        setLoading(true);
        try {
            const data = await instance.get(
                `api/manualinvoices/items/${selectedPaymentItem.id}`
            );
            setInvoicePayments(data || []);
        } catch (error) {
            console.error("Error fetching invoice payments:", error);
            toast.error("Failed to load payments");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoicePayments();
    }, [selectedPaymentItem]);

    const updateInvoicePayment = async () => {
        if (!selectedPaymentId) return;
        try {
            const data = await instance.put(
                `/api/invoicepayments/processpiip/${selectedPaymentId.id}`
            );
            const cn = selectedPaymentId?.contractNumber;
            if (cn && typeof cn === "string" && cn.trim() !== "") {
                callActiveInactiveCron(cn);
            }
            fetchInvoicePayments();
            getInvoices();
            onClose();
            toast.success(data || " Processed Successfully");
        } catch (error) {
            toast.error(error?.message || "Something went wrong");
        } finally {
            handleAllcatedConfrmCancel();
        }
    };

    const handleAllcatedConfrmCancel = () => {
        setIsAllocatedConfirm(false);
    };

    const approveInvoiceItems = async () => {
        if (!selectedPaymentItem) return;
        setLoading(true);
        try {
            await instance.put(
                `/api/manualinvoices/issueinvoice/${selectedPaymentItem.id}?zerovat=${selectedPaymentItem?.attributes?.zeroVat || false}`
            );
            fetchInvoicePayments();
            getInvoices();
            onClose();
            toast.success("Invoice Generate Successfully");
        } catch (error) {
            console.error("Error issueinvoice invoice:", error);
            toast.error("Failed to issueinvoice invoice");
        } finally {
            setLoading(false);
        }
    };

    const isAlreadyRejected = (() => {
        const status = String(
            selectedPaymentItem?.status ||
                selectedPaymentItem?.attributes?.status ||
                ""
        ).toLowerCase();
        return (
            selectedPaymentItem?.rejected === true ||
            status === "rejected" ||
            status === "reject"
        );
    })();

    const canIssueOrReject =
        checkPrivileges("manualInvoiceIssueInvoice") &&
        !selectedPaymentItem?.issueInvoice &&
        !isAlreadyRejected;

    const rejectInvoiceItems = async () => {
        if (!selectedPaymentItem?.id) return;
        setLoading(true);
        try {
            await instance.put(
                `/api/manualinvoices/reject/${selectedPaymentItem.id}`
            );
            getInvoices();
            onClose();
            toast.success("Manual invoice rejected");
        } catch (error) {
            console.error("Error reject manual invoice:", error);
            toast.error(error?.message || "Failed to reject manual invoice");
        } finally {
            setLoading(false);
            setIsRejectConfirm(false);
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    Invoice No: {selectedPaymentItem?.refNum || ""}
                </DialogTitle>
                <PaymentSummary
                    saleTotal={selectedPaymentItem?.saleTotal || 0}
                    saleVat={selectedPaymentItem?.saleVat || 0}
                    saleNet={selectedPaymentItem?.saleNet || 0}
                />
                <Typography variant="h6" sx={{ px: 3, py: 2 }}>
                    List Of Items
                </Typography>
                <DialogContent dividers sx={{ maxHeight: 400, overflowY: "auto", p: 0 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                py: 4,
                            }}
                        >
                            <CircularProgress color="success" />
                        </Box>
                    ) : invoicePayments.length > 0 ? (
                        <>
                            <Box
                                sx={{
                                    ...rowSx,
                                    fontWeight: "bold",
                                    borderBottom: "2px solid #ddd",
                                    backgroundColor: "#fafafa",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 1,
                                }}
                            >
                                <Box>Item</Box>
                                <Box>Quantity</Box>
                                <Box>Sale Total</Box>
                                <Box>Sale Vat</Box>
                                <Box>Sale Net</Box>
                                <Box>Category</Box>
                                <Box>Expense Type</Box>
                            </Box>
                            {invoicePayments.slice(0, visibleCount).map((item, index) => (
                                <Box
                                    key={item.id || index}
                                    sx={{
                                        ...rowSx,
                                        "&:hover": { backgroundColor: "#f5f5f5" },
                                        borderBottom: "1px solid #eee",
                                    }}
                                >
                                    <Box sx={{ wordBreak: "break-word" }}>
                                        {item?.itemName || "-"}
                                        {item?.itemCode ? ` - ${item.itemCode}` : ""}
                                    </Box>
                                    <Box>{item?.quantity ?? item?.itemQty ?? 0}</Box>
                                    <Box>
                                        {item?.saleTotal != null
                                            ? Number(item.saleTotal).toFixed(2)
                                            : "0.00"}
                                    </Box>
                                    <Box>
                                        {item?.saleVat != null
                                            ? Number(item.saleVat).toFixed(2)
                                            : "0.00"}
                                    </Box>
                                    <Box>
                                        {item?.saleNet != null
                                            ? Number(item.saleNet).toFixed(2)
                                            : "0.00"}
                                    </Box>
                                    <Box sx={{ wordBreak: "break-word" }}>
                                        {item?.category || "-"}
                                    </Box>
                                    <Box sx={{ wordBreak: "break-word" }}>
                                        {item?.expenseType || "-"}
                                    </Box>
                                </Box>
                            ))}
                            {visibleCount < invoicePayments.length && (
                                <Box sx={{ textAlign: "center", py: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={handleLoadMore}
                                        size="small"
                                    >
                                        Load More
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 2, textAlign: "center" }}
                        >
                            No payment details available.
                        </Typography>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                    <Button
                        color="error"
                        onClick={() => setIsRejectConfirm(true)}
                        disabled={!canIssueOrReject || loading}
                    >
                        Reject
                    </Button>
                    <Button
                        onClick={approveInvoiceItems}
                        disabled={!canIssueOrReject || loading}
                    >
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            {isAllocatedConfirm && (
                <ConfirmDialoag
                    onCancel={handleAllcatedConfrmCancel}
                    onOk={updateInvoicePayment}
                    title={"Are You Sure?"}
                    children={selectedPaymentId?.contractNumber?.toUpperCase()}
                />
            )}

            {isRejectConfirm && (
                <ConfirmDialoag
                    onCancel={() => setIsRejectConfirm(false)}
                    onOk={rejectInvoiceItems}
                    title="Reject Manual Invoice?"
                    children="No invoice will be generated. Only the status will change."
                />
            )}
        </>
    );
};

export default ListItemsInvoicebase;
